import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { useClient } from '@/context/ClientContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { UserPlus, Check, X, Trash2, Mail, Shield, Users, Briefcase } from 'lucide-react';
import { toast } from 'sonner';

const ROLE_META = {
  admin: { label: 'Admin', icon: Shield, color: 'text-primary' },
  member: { label: 'Member', icon: Users, color: 'text-emerald-600 dark:text-emerald-400' },
  client: { label: 'Client', icon: Briefcase, color: 'text-amber-600 dark:text-amber-400' },
};

const STATUS_META = {
  pending_request: { label: 'Access requested', tone: 'warning' },
  approved: { label: 'Approved · awaiting login', tone: 'info' },
  accepted: { label: 'Active', tone: 'success' },
  revoked: { label: 'Revoked', tone: 'destructive' },
};

export default function Team() {
  const { clients } = useClient();
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(blankForm());

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/invites');
      setInvites(data || []);
    } catch (e) {
      if (e.response?.status === 403) toast.error('Only admins can manage the team.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e?.preventDefault();
    if (!form.email.trim() || !form.first_name.trim()) {
      return toast.error('First name and email are required');
    }
    if (form.role === 'client' && !form.client_id) {
      return toast.error('Choose a client for client-role invites');
    }
    try {
      await api.post('/invites', {
        email: form.email.trim().toLowerCase(),
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        role: form.role,
        client_id: form.role === 'client' ? form.client_id : null,
      });
      setForm(blankForm());
      await load();
      toast.success('Invite created — they can sign in with Google now.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create invite');
    }
  };

  const approve = async (inv) => {
    try {
      await api.patch(`/invites/${inv.invite_id}/approve`, {
        first_name: inv.first_name || undefined,
        last_name: inv.last_name || undefined,
        role: inv.role || 'member',
        client_id: inv.client_id ?? null,
      });
      await load();
      toast.success(`Access approved for ${inv.email}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Approve failed');
    }
  };

  const revoke = async (inv) => {
    if (!window.confirm(`Revoke access for ${inv.email}? Their active sessions will be terminated.`)) return;
    await api.patch(`/invites/${inv.invite_id}/revoke`);
    await load();
    toast.success('Access revoked');
  };

  const removeInvite = async (inv) => {
    if (!window.confirm(`Delete invite for ${inv.email}?`)) return;
    await api.delete(`/invites/${inv.invite_id}`);
    await load();
  };

  const updateInviteField = (inv, key, value) => {
    setInvites(list =>
      list.map(i => (i.invite_id === inv.invite_id ? { ...i, [key]: value } : i))
    );
  };

  const requests = invites.filter(i => i.status === 'pending_request');
  const team = invites.filter(i => i.status !== 'pending_request');

  return (
    <div className="space-y-6" data-testid="team-page">
      <div>
        <div className="text-xs uppercase tracking-widest text-muted-foreground">Access control</div>
        <h1 className="mt-2 font-display text-3xl tracking-tight">Team & invites</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          AuthorityOS™ is invite-only. Add your team or your clients here — they sign in with Google using the
          exact email you enter below.
        </p>
      </div>

      <Tabs defaultValue={requests.length ? 'requests' : 'invite'}>
        <TabsList>
          <TabsTrigger value="invite" data-testid="team-tab-invite">
            <UserPlus className="h-4 w-4 mr-1" /> Invite
          </TabsTrigger>
          <TabsTrigger value="requests" data-testid="team-tab-requests">
            Access requests {requests.length > 0 && <Badge className="ml-2" variant="destructive">{requests.length}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="members" data-testid="team-tab-members">
            Members {team.length > 0 && <span className="ml-2 text-xs text-muted-foreground">{team.length}</span>}
          </TabsTrigger>
        </TabsList>

        {/* INVITE */}
        <TabsContent value="invite" className="mt-6">
          <form onSubmit={submit} className="rounded-xl border border-border bg-card p-6 space-y-5">
            <div className="text-sm font-medium">Pre-approve a new user</div>
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="First name *">
                <Input data-testid="team-invite-first" value={form.first_name} onChange={e => setForm({ ...form, first_name: e.target.value })} />
              </Field>
              <Field label="Last name">
                <Input data-testid="team-invite-last" value={form.last_name} onChange={e => setForm({ ...form, last_name: e.target.value })} />
              </Field>
              <Field label="Email * (must match their Google login email)">
                <Input data-testid="team-invite-email" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="person@example.com" />
              </Field>
              <Field label="Role *">
                <Select value={form.role} onValueChange={v => setForm({ ...form, role: v, client_id: v === 'client' ? form.client_id : '' })}>
                  <SelectTrigger data-testid="team-invite-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin — full access, can invite others</SelectItem>
                    <SelectItem value="member">Member — internal team, no invite rights</SelectItem>
                    <SelectItem value="client">Client — read-only access to one client portal</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              {form.role === 'client' && (
                <Field label="Assigned client *">
                  <Select value={form.client_id} onValueChange={v => setForm({ ...form, client_id: v })}>
                    <SelectTrigger data-testid="team-invite-client"><SelectValue placeholder="Choose a client portal" /></SelectTrigger>
                    <SelectContent>
                      {clients.map(c => <SelectItem key={c.client_id} value={c.client_id}>{c.company_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
              )}
            </div>
            <div className="flex justify-end">
              <Button type="submit" data-testid="team-invite-submit">
                <UserPlus className="h-4 w-4 mr-1" /> Create invite
              </Button>
            </div>
            <div className="rounded-md bg-secondary/50 p-4 text-xs text-muted-foreground">
              How it works: When you create an invite, the person can immediately sign in at the login page using
              <span className="font-medium text-foreground"> Continue with Google</span>. Their Google email must
              match the email you entered here.
            </div>
          </form>
        </TabsContent>

        {/* ACCESS REQUESTS */}
        <TabsContent value="requests" className="mt-6 space-y-3">
          {requests.length === 0 && (
            <Empty text="No pending access requests." />
          )}
          {requests.map(inv => (
            <div key={inv.invite_id} className="rounded-xl border border-border bg-card p-5">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline">requested {timeAgo(inv.created_at)}</Badge>
                  </div>
                  <div className="mt-2 font-medium flex items-center gap-2">
                    <Mail className="h-4 w-4 text-muted-foreground" /> {inv.email}
                  </div>
                  {inv.google_name && (
                    <div className="text-xs text-muted-foreground mt-1">Google name: {inv.google_name}</div>
                  )}
                </div>
              </div>
              <div className="mt-4 grid md:grid-cols-4 gap-3">
                <Input placeholder="First name" value={inv.first_name || ''} onChange={e => updateInviteField(inv, 'first_name', e.target.value)} />
                <Input placeholder="Last name" value={inv.last_name || ''} onChange={e => updateInviteField(inv, 'last_name', e.target.value)} />
                <Select value={inv.role || 'member'} onValueChange={v => updateInviteField(inv, 'role', v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="member">Member</SelectItem>
                    <SelectItem value="client">Client</SelectItem>
                  </SelectContent>
                </Select>
                {inv.role === 'client' ? (
                  <Select value={inv.client_id || ''} onValueChange={v => updateInviteField(inv, 'client_id', v)}>
                    <SelectTrigger><SelectValue placeholder="Client portal" /></SelectTrigger>
                    <SelectContent>
                      {clients.map(c => <SelectItem key={c.client_id} value={c.client_id}>{c.company_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : <div />}
              </div>
              <div className="mt-4 flex gap-2 justify-end">
                <Button size="sm" variant="outline" onClick={() => removeInvite(inv)}>
                  <X className="h-4 w-4 mr-1" /> Deny
                </Button>
                <Button size="sm" onClick={() => approve(inv)}>
                  <Check className="h-4 w-4 mr-1" /> Approve
                </Button>
              </div>
            </div>
          ))}
        </TabsContent>

        {/* MEMBERS */}
        <TabsContent value="members" className="mt-6 space-y-2">
          {team.length === 0 && <Empty text="No team members yet. Use the Invite tab to add someone." />}
          {team.map(inv => {
            const meta = ROLE_META[inv.role] || ROLE_META.member;
            const Icon = meta.icon;
            const status = STATUS_META[inv.status] || { label: inv.status, tone: 'info' };
            const linkedClient = clients.find(c => c.client_id === inv.client_id);
            return (
              <div key={inv.invite_id} className="rounded-xl border border-border bg-card p-4 flex items-center justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="font-medium flex items-center gap-2">
                    <Icon className={`h-4 w-4 ${meta.color}`} />
                    {inv.first_name} {inv.last_name}
                    <Badge variant="outline" className="text-xs capitalize">{meta.label}</Badge>
                  </div>
                  <div className="text-xs text-muted-foreground flex items-center gap-2 mt-1">
                    <Mail className="h-3 w-3" /> {inv.email}
                    {linkedClient && <span>· portal: {linkedClient.company_name}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant={status.tone === 'destructive' ? 'destructive' : 'secondary'}>{status.label}</Badge>
                  {inv.status !== 'revoked' && (
                    <Button size="sm" variant="ghost" onClick={() => revoke(inv)} title="Revoke">
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => removeInvite(inv)} title="Delete">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            );
          })}
        </TabsContent>
      </Tabs>

      {loading && <div className="text-xs text-muted-foreground">Refreshing…</div>}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wider text-muted-foreground">{label}</Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}

function Empty({ text }) {
  return <div className="rounded-xl border border-dashed border-border p-10 text-center text-sm text-muted-foreground">{text}</div>;
}

function blankForm() {
  return { email: '', first_name: '', last_name: '', role: 'member', client_id: '' };
}

function timeAgo(iso) {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}
